import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import type { Active, CollisionDetection, DndContextProps, DragEndEvent, DragStartEvent, DropAnimation, MeasuringConfiguration, UniqueIdentifier } from '@dnd-kit/core';
import { DndContext, KeyboardSensor, MeasuringStrategy, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { SortableItem } from './components/SortableItem';
import { SortableOverlay } from './components/SortableOverlay';
import { VirtualFixedList } from '@shared/components/VirtualScroll';
import { restrictToVerticalAxis, restrictToFirstScrollableAncestor } from '@dnd-kit/modifiers';
interface BaseItem {
  id: UniqueIdentifier;
}

interface SortableListProps<T extends BaseItem> {
  items: T[];
  onChange: (items: T[]) => void;
  renderItem: (item: T, index: number, isDragActive?: boolean) => React.ReactNode;
  hasDragHandle?: boolean;
  handleDragStart?: (activeId: string) => void;
  handleDragEnd?: (dragData: { fromIndex: number; toIndex: number; items: T[]; reorderedItem: T | undefined }) => void;
  handleDragCancel?: () => void;
  renderVirtualList?: boolean;
  itemSize?: number;
  adjustOverlayScale?: boolean;
  customStyle?: React.CSSProperties;
  dragPlaceholderOpacity?: number;
  collisionDetection?: CollisionDetection;
  disabled?: boolean;
  /**
   * Switch droppable measurement from dnd-kit's default `WhileDragging` strategy to `Always`.
   *
   * Named for the strategy it selects, not for the behaviour it adds: `remeasureWhileDragging` would
   * have named the *default*, since `defaultMeasuringConfiguration.droppable.strategy` is already
   * `MeasuringStrategy.WhileDragging`.
   *
   * Only needed when items change size *during* a drag (e.g. the block editor folds every block down
   * to a uniform row on drag start). Without it, dnd-kit keeps the rects it captured before the
   * resize and drops land on the wrong item.
   *
   * What it actually changes is `useDroppableMeasuring`'s `isDisabled()`, which returns `false`
   * unconditionally under `Always` instead of `!dragging` — so measurement stops being gated on drag
   * state, buying a pass around activation plus recomputes when the container set changes. It is NOT
   * a per-frame layout read: the interval path is guarded by `typeof frequency !== 'number'`, and
   * because this overrides only `strategy`, `frequency` stays `MeasuringFrequency.Optimized` — the
   * string `'optimized'` — so that timer never arms. If continuous measurement is ever genuinely
   * needed, `droppable.frequency` has to become a number for that path to run at all.
   */
  alwaysMeasureDroppables?: boolean;
  /** Overrides the drag overlay's drop animation. Pass `null` to drop instantly. */
  dropAnimation?: DropAnimation | null;
  /**
   * Merged over the drag overlay's own positioning styles.
   *
   * dnd-kit sizes the overlay from the rect it captured at drag start, so a list whose items resize
   * during a drag needs to correct the height here — both to place the overlay's contents under the
   * cursor and because that box is what the modifiers clamp against.
   */
  overlayStyle?: React.CSSProperties;
  /**
   * Turn off dnd-kit's one-shot scroll correction.
   *
   * dnd-kit corrects the scroll position once, right after activation, if the active item has moved
   * since it was measured — but a list whose items *animate* their height at drag start has not
   * moved anything yet at that point, so the correction reads zero and disarms itself. A consumer
   * doing its own anchoring wants it out of the way entirely rather than firing a partial correction
   * mid-animation.
   *
   * A boolean rather than dnd-kit's `autoScroll` object: this wrapper exists to hide that library
   * from its consumers, and `{ layoutShiftCompensation: false }` is the only shape any of them needs.
   */
  disableLayoutShiftCompensation?: boolean;
}

// Hoisted: `useMeasuringConfiguration` memoizes on `config.droppable` identity, so an inline literal
// would re-derive the merged configuration on every render.
const MEASURE_DROPPABLES_ALWAYS: MeasuringConfiguration = { droppable: { strategy: MeasuringStrategy.Always } };
const NO_LAYOUT_SHIFT_COMPENSATION: DndContextProps['autoScroll'] = { layoutShiftCompensation: false };

export const SortableList = <T extends BaseItem>(props: SortableListProps<T>) => {
  const {
    items,
    renderItem,
    onChange,
    hasDragHandle = false,
    handleDragStart = () => {},
    handleDragEnd = () => {},
    handleDragCancel = () => {},
    renderVirtualList = false,
    itemSize = 0,
    adjustOverlayScale = true,
    customStyle = {},
    dragPlaceholderOpacity,
    collisionDetection,
    disabled,
    alwaysMeasureDroppables = false,
    dropAnimation,
    overlayStyle,
    disableLayoutShiftCompensation = false,
  } = props;

  const [active, setActive] = useState<Active | null>(null);

  const activeItem = useMemo(() => items.find(item => item.id === active?.id), [active, items]);
  const activeItemIndex = useMemo(() => items.findIndex(item => item.id === active?.id), [active, items]);

  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
    useSensor(TouchSensor),
  );

  const onDragStart = ({ active }: DragStartEvent) => {
    setActive(active);
    handleDragStart(String(active.id));
  };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    let activeIndex = -1;
    let overIndex = -1;
    let updatedItems: T[] = [];
    const selectedItem = activeItem;
    if (over && active.id !== over?.id) {
      activeIndex = items.findIndex(({ id }) => id === active.id);
      overIndex = items.findIndex(({ id }) => id === over.id);
      updatedItems = arrayMove(items, activeIndex, overIndex);
      onChange(updatedItems);
    }
    setActive(null);
    handleDragEnd({ fromIndex: activeIndex, toIndex: overIndex, items: updatedItems, reorderedItem: selectedItem });
  };

  const onDragCancel = () => {
    setActive(null);
    handleDragCancel();
  };

  return (
    <DndContext
      sensors={sensors}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragCancel={onDragCancel}
      collisionDetection={collisionDetection}
      measuring={alwaysMeasureDroppables ? MEASURE_DROPPABLES_ALWAYS : undefined}
      modifiers={[restrictToVerticalAxis, restrictToFirstScrollableAncestor]}
      autoScroll={disableLayoutShiftCompensation ? NO_LAYOUT_SHIFT_COMPENSATION : undefined}
    >
      <SortableContext
        items={items}
        strategy={verticalListSortingStrategy}
      >
        {renderVirtualList ? (
          <VirtualFixedList
            itemData={items}
            itemCount={items.length}
            overscanCount={3}
            itemSize={() => itemSize}
          >
            {({ data, index, style }) => (
              <div
                style={style}
                className={`virtual-wrapper-${data[index].id}`}
              >
                <SortableItem
                  key={data[index].id}
                  id={data[index].id}
                  hasDragHandle={hasDragHandle}
                  customStyle={customStyle}
                  dragPlaceholderOpacity={dragPlaceholderOpacity}
                  disabled={disabled}
                >
                  {renderItem(data[index], index, active?.id === data[index].id)}
                </SortableItem>
              </div>
            )}
          </VirtualFixedList>
        ) : (
          items.map((item, index) => (
            <SortableItem
              key={item.id}
              id={item.id}
              hasDragHandle={hasDragHandle}
              customStyle={customStyle}
              dragPlaceholderOpacity={dragPlaceholderOpacity}
              disabled={disabled}
            >
              {renderItem(item, index, active?.id === item.id)}
            </SortableItem>
          ))
        )}
      </SortableContext>
      {createPortal(
        <SortableOverlay
          adjustScale={adjustOverlayScale}
          dropAnimation={dropAnimation}
          style={overlayStyle}
        >
          {activeItem ? renderItem(activeItem, activeItemIndex, true) : null}
        </SortableOverlay>,
        document.body,
      )}
    </DndContext>
  );
};
